import classNames from 'classnames';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect } from 'react';
import { Icon } from '@udemy/icons';
import { BlockList } from '@udemy/react-core-components';
import { useHeaderMenuStore } from '../contexts/header-menu-context';
import styles from "./explore-menu.module.css";

/**
 * Reusable BlockList loader component with configurable item count.
 * Displays skeleton loading states while navigation data is being fetched.
 * Exported for use in nav configs (e.g. Get Certified) that need the same loading UX.
 */

export const BlockListLoader = ({
  itemCount = 8,
  className,
  title,
  dataTestId = 'block-list'
}) => {
  return /*#__PURE__*/React.createElement("div", {
    className: styles['loader-container']
  }, title && /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['section-title'], 'ud-heading-xs')
  }, title), /*#__PURE__*/React.createElement(BlockList, {
    size: "small",
    className: className,
    iconAlignment: "right",
    "data-testid": dataTestId
  }, Array.from({
    length: itemCount
  }, (_, i) => /*#__PURE__*/React.createElement(BlockList.Item, {
    key: `loading-${i}`,
    loading: true
  }))));
};

/**
 * Navigation level structure
 * Represents a single column in the multi-level navigation
 */

/**
 * Props for NavigationColumn component
 */

/**
 * NavigationColumn component
 * Renders a single column of navigation items with sections
 */
const NavigationColumn = observer(({
  level,
  onItemMouseEnter,
  columnHeader
}) => {
  const store = useHeaderMenuStore();
  const currentLevelItem = store.getActiveItemAtLevel(level.level);
  const isRootLevel = level.level === 0;

  // Auto-load sections with fetchChildren when they are first encountered
  useEffect(() => {
    level.organizedSections.forEach(section => {
      var _section$section;
      if ((_section$section = section.section) !== null && _section$section !== void 0 && _section$section.fetchChildren) {
        store.handleSectionLoad(section.section.id).catch(() => {
          // Error handling can be implemented by the navigation config if needed
        });
      }
    });
  }, [level.organizedSections, level.level, store]);

  // All columns use the same base class, with conditional border
  const columnClassName = classNames(styles['navigation-column'], !level.isLastLevel && styles['navigation-column--bordered']);

  /**
   * Render a single navigation item
   */
  const renderItem = item => {
    var _item$children$length, _item$children, _item$metadata;
    const isActive = (currentLevelItem === null || currentLevelItem === void 0 ? void 0 : currentLevelItem.id) === item.id;
    const hasChildren = ((_item$children$length = (_item$children = item.children) === null || _item$children === void 0 ? void 0 : _item$children.length) !== null && _item$children$length !== void 0 ? _item$children$length : 0) > 0 || item.fetchChildren;

    // Right-side icon: chevron when item has children, otherwise custom renderIcon
    const renderRightIcon = () => {
      if (hasChildren) {
        return /*#__PURE__*/React.createElement(Icon, {
          glyph: "next",
          size: "xsmall",
          label: false,
          "data-testid": "icon-next"
        });
      }
      if (item.renderIcon) {
        return item.renderIcon();
      }
      return null;
    };

    // Content: optional leading icon (e.g. Google logo) + title when item has children and custom renderIcon
    const content = hasChildren && item.renderIcon ? item.contentClassName ? /*#__PURE__*/React.createElement("div", {
      className: item.contentClassName
    }, item.renderIcon(), /*#__PURE__*/React.createElement("span", null, item.title)) : /*#__PURE__*/React.createElement(React.Fragment, null, item.renderIcon(), /*#__PURE__*/React.createElement("span", null, item.title)) : item.title;

    // When item has no url, render as button so it's keyboard focusable (e.g. "Learn AI" with absolute_url: 'none')
    const isFocusableButton = !item.url;
    return /*#__PURE__*/React.createElement("div", {
      key: item.id,
      onMouseEnter: () => onItemMouseEnter(item, level.level),
      "data-testid": "block-list-item"
    }, /*#__PURE__*/React.createElement(BlockList.Item, {
      href: item.url,
      componentClass: isFocusableButton ? 'button' : undefined,
      className: classNames(styles['item'], isActive && styles['item--active'], (_item$metadata = item.metadata) === null || _item$metadata === void 0 ? void 0 : _item$metadata.className),
      icon: renderRightIcon(),
      onFocus: hasChildren ? () => onItemMouseEnter(item, level.level) : undefined,
      onClick: item.onClick
    }, content));
  };

  /**
   * Render all sections in this column
   */
  const renderSections = () => {
    const sections = level.organizedSections;

    // Handle the case where there are no sections but parent is loading
    if (sections.length === 0) {
      // Check if the parent item is loading using the lookup map
      const parentLoadingState = level.parentItem ? store.isNodeLoading(level.parentItem.id) : false;
      if (parentLoadingState && level.parentItem) {
        var _level$parentItem$ren, _level$parentItem;
        // Try to get custom loading state from parent item
        const customLoadingState = (_level$parentItem$ren = (_level$parentItem = level.parentItem).renderLoadingState) === null || _level$parentItem$ren === void 0 ? void 0 : _level$parentItem$ren.call(_level$parentItem);
        return customLoadingState || null;
      }

      // No sections and no loading state
      return null;
    }
    return sections.map((section, sectionIndex) => {
      var _section$section2;
      // Check if the section or parent is loading using the lookup map
      let isLoading = false;
      let loadingNode;
      if (section.section) {
        // Check if section is actively loading OR if it has fetchChildren but no children yet
        const hasNoChildren = !section.section.children || section.section.children.length === 0;
        const shouldLoad = !!(section.section.fetchChildren && hasNoChildren);
        isLoading = store.isNodeLoading(section.section.id) || shouldLoad;
        loadingNode = section.section;
      } else if (level.parentItem) {
        isLoading = store.isNodeLoading(level.parentItem.id);
        loadingNode = level.parentItem;
      }
      const renderLoadingState = () => {
        var _loadingNode$renderLo, _loadingNode;
        if (!loadingNode) return null;

        // Try to get custom loading state from the loading node
        const customLoadingState = (_loadingNode$renderLo = (_loadingNode = loadingNode).renderLoadingState) === null || _loadingNode$renderLo === void 0 ? void 0 : _loadingNode$renderLo.call(_loadingNode);
        return customLoadingState || null;
      };
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: sectionIndex
      }, section.needsBorder && /*#__PURE__*/React.createElement("div", {
        className: styles['section-divider']
      }), ((_section$section2 = section.section) === null || _section$section2 === void 0 ? void 0 : _section$section2.sectionTitle) && /*#__PURE__*/React.createElement("div", {
        className: classNames(styles['section-title'], 'ud-heading-xs')
      }, section.section.sectionTitle, section.section.renderDecoration && section.section.renderDecoration()), isLoading ? renderLoadingState() : /*#__PURE__*/React.createElement(BlockList, {
        size: "small",
        className: styles['section'],
        iconAlignment: "right"
      }, section.items.map(renderItem)));
    });
  };
  return /*#__PURE__*/React.createElement("div", {
    className: columnClassName
  }, isRootLevel && columnHeader && /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['column-header'], 'ud-heading-sm')
  }, columnHeader), renderSections());
});

/**
 * ExploreMenu component
 * Main navigation menu that displays multi-level navigation structure
 * Uses MST store for state management and supports lazy loading of navigation items
 */
export const ExploreMenu = observer(() => {
  const store = useHeaderMenuStore();

  // Get the navigation structure and loading state from the store
  const isLoading = store.isGlobalLoading;

  // Simple handler that delegates to store
  const handleLevelMouseEnter = useCallback((item, level) => {
    store.handleItemActivated(item, level).catch(() => {
      // Error handling can be implemented by the navigation config if needed
    });
  }, [store]);
  const handleMouseLeave = useCallback(() => {
    store.clearNavigationState();
  }, [store]);

  // Get computed navigation levels from store
  const navigationLevels = store.visibleNavigationLevels;
  if (isLoading) {
    // Show default loading state for root level
    return /*#__PURE__*/React.createElement(BlockListLoader, {
      itemCount: 8
    });
  }
  return /*#__PURE__*/React.createElement("div", {
    className: styles['explore-menu-container'],
    onMouseLeave: handleMouseLeave
  }, navigationLevels.map((navLevel, levelIndex) => {
    var _store$navigationStru;
    return /*#__PURE__*/React.createElement(NavigationColumn, {
      key: levelIndex,
      level: navLevel,
      onItemMouseEnter: handleLevelMouseEnter,
      columnHeader: levelIndex === 0 ? (_store$navigationStru = store.navigationStructure) === null || _store$navigationStru === void 0 ? void 0 : _store$navigationStru.sectionTitle : undefined
    });
  }));
});
//# sourceMappingURL=explore-menu.react-component.js.map